import { IAPIRoutes } from "../../../../config";

export type INavigatorRepository = ReturnType<
  typeof NavigatorRepositoryFactory
>;

export const NavigatorRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST, PUT, DELETE } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const createInitialVisitAPI: ICreateInitialVisitAPI = async (payload) => {
    const url = APIRoutes.apiPostIPVNote
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const dataToSend = {
      type: payload.type,
      shortlisted: payload.shortlisted,
      notes: payload.notes,
      file_link: payload.file_link,
      total_time: payload.total_time,
      context: payload.context,
      doc_type: payload.doc_type,
    };

    return POST(url, dataToSend, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getIPVNotesByStatusAPI: IGetIPVNotesByStatusAPI = async (payload) => {
    const url = APIRoutes.apiGetIPVNotesByClinicID.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );

    const params: Record<string, number | undefined> = {
      status: payload.status,
    };

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getIPVNotesByStatusAPIv3: IGetIPVNotesByStatusAPIv3 = async (
    payload
  ) => {
    const url = APIRoutes.apiGetIPVNotesByClinicIDv3.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );

    const params: Record<string, number | undefined> = {
      note_type: payload.status,
    };

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const putIPVNoteAPI: IPutIPVNoteAPI = async (payload) => {
    const url = APIRoutes.apiPutIPVNote
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{ipv_note_id}", payload.ipv_note_id.toString());

    const dataToSend = {
      type: payload.type,
      shortlisted: payload.shortlisted,
      notes: payload.notes,
      file_link: payload.file_link,
      total_time: payload.total_time,
      context: payload.context,
      doc_type: payload.doc_type,
    };

    return PUT(url, dataToSend, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getIPVNotesByPatientIDAPI: IGetIPVNotesByPatientIDAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiGetIPVNotesByPatientID
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const deleteIPVNoteAPI: IDeleteIPVNoteAPI = async (payload) => {
    const url = APIRoutes.apiDeleteIPVNote
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{ipv_note_id}", payload.ipv_note_id.toString());

    return DELETE(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const deleteIPVDraftAndSessionAPI: IDeleteIPVDraftAndSessionAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiDeleteIPVDraftAndSession
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const dataToSend: {
      ipv_note_id?: number;
      session_id?: string;
    } = {};

    if (payload.ipv_note_id) {
      dataToSend.ipv_note_id = payload.ipv_note_id;
    }
    if (payload.session_id) {
      dataToSend.session_id = payload.session_id;
    }

    return DELETE(url, dataToSend, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getPatientsForNavigatorAPI: IGetPatientsForNavigatorAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiGetPatientsForNavigator.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const updatePatientExtraInfoAPI: IUpdatePatientExtraInfoAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiPostPatientExtraInfo
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const dataToSend = {
      extra_info: payload.extra_info,
    };

    return POST(url, dataToSend, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getPatientVisitNotesAPI: IGetPatientVisitNotesAPI = async (payload) => {
    const url = APIRoutes.apiGetPatientVisitNotes
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const storePreChartNoteAPI: IStorePreChartNoteAPI = async (payload) => {
    const url = APIRoutes.apiStorePreChartNote.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return PUT(
      url,
      {
        apptNote: payload.apptNote,
        patient_id: payload.patient_id,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  return {
    createInitialVisitAPI,
    getIPVNotesByStatusAPI,
    putIPVNoteAPI,
    getIPVNotesByPatientIDAPI,
    deleteIPVNoteAPI,
    deleteIPVDraftAndSessionAPI,
    getIPVNotesByStatusAPIv3,
    getPatientsForNavigatorAPI,
    updatePatientExtraInfoAPI,
    getPatientVisitNotesAPI,
    storePreChartNoteAPI,
  };
};
